import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Calendar, User, Building2, CreditCard, FileText, Mail, Phone, Globe, ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';

// Interfaces para tipagem dos dados do Supabase
interface CustomerData {
  id: string;
  name: string;
  legal_name: string | null;
}

interface CustomerRelationshipData {
  id: string;
  customer_id: string;
  child_id: string;
  relationship?: string;
  child: CustomerData;
}

interface SubscriptionPlanData {
  id: string;
  name: string;
}

interface SubscriptionItemData {
  id: string;
  quantity: number;
  unit_price: number;
  current_term_start: string;
  current_term_end: string;
  currency_code: string;
  billing_cycles?: number;
  billing_by?: string;
  term_start?: string;
  term_end?: string;
  subscription_plans: SubscriptionPlanData;
}

interface CustomerContactData {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
}

interface SubscriptionContactData {
  id: string;
  contact_type: string;
  customer_contacts: CustomerContactData;
}

interface SubscriptionData {
  id: string;
  display_id: string;
  customer: {
    id: string;
    name: string;
    legal_name: string;
  };
  relationship: {
    id: string;
    relationship?: string;
  };
  start_date: string;
  currency_code: string;
  po_number: string | null;
  payment_source_id: string | null;
  net_term_days: number;
  items: Array<{
    id: string;
    subscription_plan: {
      id: string;
      name: string;
    };
    quantity: number;
    unit_price: number;
    billing_period: string;
    billing_period_unit: string;
    current_term_start: string;
    current_term_end: string;
  }>;
  contacts: Array<{
    id: string;
    contact_type: string;
    contact: {
      id: string;
      first_name: string;
      last_name: string | null;
      email: string;
      phone: string | null;
    };
  }>;
}

interface SubscriptionResponseData {
  id: string;
  display_id: string;
  start_date: string;
  po_number: string | null;
  payment_source_id: string | null;
  net_term_days: number;
  customer_relationship: CustomerRelationshipData;
}

const SubscriptionConfirmation = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('ID da assinatura não fornecido');
      setLoading(false);
      return;
    }

    fetchSubscriptionData(id);
  }, [id]);

  const fetchSubscriptionData = async (subscriptionId: string) => {
    setLoading(true);
    setError('');
    try {
      // Buscar dados principais da assinatura
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          display_id,
          start_date,
          po_number,
          payment_source_id,
          net_term_days,
          customer_relationship(
            id,
            customer_id,
            child_id,
            relationship,
            child:customers!fk_child(
              id,
              name,
              legal_name
            )
          )
        `)
        .eq('id', subscriptionId)
        .single();

      if (subscriptionError) throw subscriptionError;
      if (!subscriptionData) throw new Error('Assinatura não encontrada');

      console.log('Subscription data fetched successfully:', subscriptionData);
      console.log('Customer relationship data:', subscriptionData.customer_relationship);

      // Buscar itens da assinatura
      const { data: itemsData, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          current_term_start,
          current_term_end,
          currency_code,
          billing_cycles,
          billing_by,
          term_start,
          term_end,
          subscription_plans (
            id,
            name
          )
        `)
        .eq('subscription_id', subscriptionId);

      if (itemsError) throw itemsError;

      // Buscar contatos da assinatura
      const { data: contactsData, error: contactsError } = await supabase
        .from('subscription_contacts')
        .select(`
          id,
          contact_type,
          customer_contacts (
            id,
            customer_id,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('subscription_id', subscriptionId);

      if (contactsError) throw contactsError;

      // Extrair dados do cliente e relacionamento
      const relationshipData = subscriptionData.customer_relationship || {
        id: '',
        relationship: '',
        child: {
          id: '',
          name: '',
          legal_name: ''
        }
      };
      
      const childData = relationshipData.child || {
        id: '',
        name: '',
        legal_name: ''
      };

      // Formatar dados para o componente
      const formattedData: SubscriptionData = {
        id: subscriptionData.id,
        display_id: subscriptionData.display_id || subscriptionData.id.substring(0, 8).toUpperCase(),
        customer: {
          id: childData.id || '',
          name: childData.name || '',
          legal_name: childData.legal_name || '',
        },
        relationship: {
          id: relationshipData.id || '',
          relationship: relationshipData.relationship || '',
        },
        start_date: subscriptionData.start_date,
        currency_code: (itemsData && itemsData.length > 0) ? itemsData[0].currency_code : 'BRL',
        po_number: subscriptionData.po_number,
        payment_source_id: subscriptionData.payment_source_id,
        net_term_days: subscriptionData.net_term_days,
        items: (itemsData || []).map(item => {
          const planData = item.subscription_plans || {
            id: '',
            name: ''
          };
          return {
            id: item.id,
            subscription_plan: {
              id: planData.id || '',
              name: planData.name || '',
            },
            quantity: item.quantity,
            unit_price: item.unit_price,
            current_term_start: item.current_term_start,
            current_term_end: item.current_term_end,
          };
        }),
        contacts: (contactsData || []).map(contact => {
          const contactData = contact.customer_contacts || {
            id: '',
            first_name: '',
            last_name: null,
            email: '',
            phone: null,
          };
          return {
            id: contact.id,
            contact_type: contact.contact_type,
            contact: {
              id: contactData.id || '',
              first_name: contactData.first_name || '',
              last_name: contactData.last_name,
              email: contactData.email || '',
              phone: contactData.phone,
            },
          };
        }),
      };

      setSubscription(formattedData);
    } catch (error: any) {
      console.error('Erro ao buscar dados da assinatura:', error);
      setError(error.message || 'Erro ao buscar dados da assinatura');
    } finally {
      setLoading(false);
    }
  };

  const getContactTypeLabel = (type: string) => {
    switch (type) {
      case 'technical':
        return 'Técnico';
      case 'administrative':
        return 'Administrativo';
      case 'billing':
        return 'Faturamento';
      case 'pedagogical':
        return 'Pedagógico';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getBillingPeriodLabel = (period: string, unit: string) => {
    if (unit === 'month') {
      return `${period} ${parseInt(period) === 1 ? 'mês' : 'meses'}`;
    } else if (unit === 'year') {
      return `${period} ${parseInt(period) === 1 ? 'ano' : 'anos'}`;
    }
    return `${period} ${unit}`;
  };

  const calculateTotalAmount = () => {
    if (!subscription) return 0;
    return subscription.items.reduce((total, item) => {
      return total + (item.quantity * item.unit_price);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="text-lg text-muted-foreground">Carregando dados da assinatura...</p>
        </div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md bg-white/60 backdrop-blur-sm border shadow-sm">
          <CardHeader>
            <CardTitle className="text-center text-red-500">Erro</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">{error || 'Não foi possível carregar os dados da assinatura'}</p>
          </CardContent>
          <CardContent className="flex justify-center">
            <Button onClick={() => navigate('/subscriptions')}>Voltar para Assinaturas</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Success Hero Section */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="mb-8 flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full ring-4 ring-white/30">
              <CheckCircle2 className="h-20 w-20 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            Assinatura criada com sucesso!
          </h1>
          
          <p className="text-xl text-white/90 mb-6">
            A assinatura #{subscription.display_id || `${subscription.id.substring(0, 6)}-${subscription.id.substring(6, 9)}`} foi criada e está pronta para uso.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
              onClick={() => navigate('/subscriptions')}
            >
              Ver Todas as Assinaturas
            </Button>
            <Button 
              size="lg"
              className="bg-white text-green-600 hover:bg-white/90 transition-all duration-300"
              onClick={() => navigate(`/subscriptions/${subscription.id}`)}
            >
              Ver Detalhes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                  Informações do Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <User className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Cliente</p>
                      <p className="font-semibold text-gray-900">{subscription.customer.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Building2 className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Razão Social</p>
                      <p className="font-semibold text-gray-900">{subscription.customer.legal_name || '-'}</p>
                    </div>
                  </div>
                </div>
                
                {subscription.relationship.relationship && (
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-blue-600">Tenant</p>
                      <p className="font-semibold text-blue-900">{subscription.relationship.relationship}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subscription Details */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Detalhes da Assinatura
                </CardTitle>
                <CardDescription>
                  ID: {subscription.display_id || `${subscription.id.substring(0, 6)}-${subscription.id.substring(6, 9)}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Data de Início</p>
                      <p className="font-semibold text-gray-900">{formatDate(subscription.start_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Globe className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Moeda</p>
                      <p className="font-semibold text-gray-900">{subscription.currency_code}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <FileText className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Número do Pedido (PO)</p>
                      <p className="font-semibold text-gray-900">{subscription.po_number || '-'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <CreditCard className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Forma de Pagamento</p>
                      <p className="font-semibold text-gray-900">
                        {subscription.payment_source_id === 'boleto' 
                          ? 'Boleto Bancário' 
                          : subscription.payment_source_id === 'transferencia' 
                            ? 'Transferência' 
                            : subscription.payment_source_id || '-'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Prazo de Pagamento</p>
                      <p className="font-semibold text-gray-900">{subscription.net_term_days} dias</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Plans */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <FileText className="h-5 w-5 mr-2 text-green-600" />
                  Planos da Assinatura
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {subscription.items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-white/50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{item.subscription_plan.name}</h3>
                          <div className="text-sm text-gray-600">
                            Vigência: {formatDate(item.current_term_start)} a {formatDate(item.current_term_end)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg text-gray-900">
                            {formatCurrency(item.unit_price * item.quantity, subscription.currency_code)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {item.quantity} x {formatCurrency(item.unit_price, subscription.currency_code)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Separator className="my-4" />
                  
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="font-semibold text-green-900">Total da Assinatura</div>
                    <div className="font-bold text-xl text-green-900">
                      {formatCurrency(calculateTotalAmount(), subscription.currency_code)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Contacts & Summary */}
          <div className="space-y-6">
            {/* Contacts */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <User className="h-5 w-5 mr-2 text-indigo-600" />
                  Contatos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscription.contacts.map((contact) => (
                  <div key={contact.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">
                        {getContactTypeLabel(contact.contact_type)}
                      </Badge>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {contact.contact.first_name} {contact.contact.last_name || ''}
                    </h4>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{contact.contact.email}</span>
                      </div>
                      {contact.contact.phone && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Phone className="h-4 w-4" />
                          <span>{contact.contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/subscriptions')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Todas as Assinaturas
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Ver Detalhes Completos
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionConfirmation; 